import { useEffect, useRef, useCallback, useState } from "react";
import { io } from "socket.io-client";
import getCookie from "../Utils/getCookie";

const deriveSocketBase = (apiBase) => {
  try {
    const { protocol, host } = new URL(apiBase);
    return `${protocol}//${host}`;
  } catch {
    return (apiBase || "http://localhost:6001").replace(/\/+$/, "");
  }
};

const SOCKET_BASE = deriveSocketBase(
  process.env.REACT_APP_BASE_URL || "http://localhost:6001"
);

/**
 * Connects to the /notification namespace and joins the personal notification room.
 * Calls handlers.onCreated(notification) whenever a new notification arrives.
 *
 * @param {object} handlers
 * @param {(notification: object) => void} [handlers.onCreated]
 */
const useNotificationSocket = (handlers = {}) => {
  const socketRef = useRef(null);
  const handlersRef = useRef(handlers);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    const token = getCookie("token-siraj");

    const socket = io(`${SOCKET_BASE}/notification`, {
      auth: { token },
      transports: ["websocket"],
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setIsConnected(true);
      socket.emit("join_notifications");
    });

    socket.on("disconnect", () => setIsConnected(false));

    socket.on("notification_created", (data) => {
      handlersRef.current.onCreated?.(data);
    });

    socket.on("error", (data) => {
      console.warn("[NotificationSocket] error:", data?.message);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, []);

  const reconnect = useCallback(() => {
    socketRef.current?.connect();
  }, []);

  return { isConnected, reconnect };
};

export default useNotificationSocket;
